import { AlertCircle, Loader2, RefreshCw, WifiOff, X } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001/"
const HEALTH_URL = `${API_BASE_URL}v1/health/`
const POLL_INTERVAL_MS = 3_000
const CHECK_TIMEOUT_MS = 5_000

interface ErrorInfo {
  status?: number
  message: string
  retryCount: number
  lastChecked: Date
}

/**
 * BackendConnectionBanner
 *
 * Self-contained component that polls /v1/health/ every 3 seconds.
 * Shows a sticky bottom banner when the backend is unreachable.
 * Automatically reloads the page once the backend comes back online,
 * which cleanly re-triggers auth and account-status initialization.
 */
export const BackendConnectionBanner: React.FC = () => {
  const [disconnected, setDisconnected] = useState(false)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<ErrorInfo | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const wasDisconnectedRef = useRef(false)
  const retryCountRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const check = async () => {
    setChecking(true)
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS)
      await fetch(HEALTH_URL, { signal: controller.signal })
      clearTimeout(timeoutId)

      // Backend is back — reload if we were previously disconnected
      if (wasDisconnectedRef.current) {
        window.location.reload()
        return
      }

      retryCountRef.current = 0
      setDisconnected(false)
      setError(null)

      // Stop polling once connected
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    } catch (err: any) {
      retryCountRef.current += 1
      wasDisconnectedRef.current = true

      const isTimeout = err.name === "AbortError"
      const message = isTimeout
        ? "Connection timed out — the backend is not responding."
        : "Unable to reach the backend server. Check that the server is running."

      setDisconnected(true)
      setDismissed(false)
      setError({ message, retryCount: retryCountRef.current, lastChecked: new Date() })
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => {
    check()
    timerRef.current = setInterval(check, POLL_INTERVAL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  if (!disconnected || dismissed) return null

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-red-700 bg-red-600 text-white shadow-[0_-4px_16px_rgba(0,0,0,0.25)]"
    >
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-4 px-4 py-3 sm:items-center">
        {/* Icon + message */}
        <div className="flex items-start gap-3 sm:items-center">
          <div className="mt-0.5 shrink-0 sm:mt-0">
            {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <WifiOff className="h-5 w-5" />}
          </div>
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-sm font-semibold">
              {checking ? "Reconnecting to server…" : "Backend server unreachable"}
            </span>
            {error && !checking && (
              <span className="text-xs text-red-100 sm:text-sm">{error.message}</span>
            )}
          </div>
        </div>

        {/* Meta + actions */}
        <div className="flex shrink-0 items-center gap-3">
          {error && !checking && (
            <div className="hidden flex-col items-end gap-0.5 text-right text-xs text-red-100 sm:flex">
              <span className="flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {error.retryCount} failed {error.retryCount === 1 ? "check" : "checks"}
              </span>
              <span>
                Last attempt:{" "}
                {error.lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            </div>
          )}

          {!checking && (
            <button
              onClick={check}
              className="flex items-center gap-1.5 rounded-md border border-red-400 bg-red-700 px-3 py-1.5 text-xs font-medium hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry now
            </button>
          )}

          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="rounded p-1 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Mobile detail row */}
      {error && !checking && (
        <div className="border-t border-red-500 px-4 py-1.5 text-xs text-red-100 sm:hidden">
          {error.retryCount} failed {error.retryCount === 1 ? "check" : "checks"} &middot; Last:{" "}
          {error.lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
      )}
    </div>
  )
}
